import { Goal, Task, Timestamp } from '@griply/types';
import { computeGoalTaskProgress } from '@griply/utils';
import { useMemo } from 'react';

import { useTimeframeDifference } from './use-timeframe-difference';

export type UseGoalTaskProgressOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  allTasks: Task[];
};

export const useGoalTaskProgress = (
  goal: Goal,
  { startDate, endDate, allTasks }: UseGoalTaskProgressOptions,
) => {
  const completedTasks = useMemo(
    () =>
      allTasks
        .filter(({ completedAt }) => !!completedAt)
        .sort(
          (taskA, taskB) =>
            taskA.completedAt!.getTime() - taskB.completedAt!.getTime(),
        ),
    [allTasks],
  );

  const nonHabitTaskCount = useMemo(
    () => allTasks.filter(({ schedules }) => !schedules?.length).length,
    [allTasks],
  );

  const { entries, target } = useMemo(
    () =>
      computeGoalTaskProgress({
        goal,
        completedTasks,
        allTaskCount: nonHabitTaskCount,
      }),
    [goal, completedTasks, nonHabitTaskCount],
  );

  const timeframeDifference = useTimeframeDifference({
    entries,
    startDate,
    endDate,
    target,
  });

  const completionPercentage =
    Math.floor((completedTasks.length / allTasks.length) * 100) || 0;

  return {
    allTasks,
    entries,
    target,
    completionPercentage,
    timeframeDifference,
  };
};
